import * as React from 'react'
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

/**
 * Column definition for DataTable
 */
export interface ColumnDef<T> {
  /** Unique key for the column */
  id: string
  /** Column header text */
  header: string
  /** Accessor function to get cell value from row data */
  accessorFn?: (row: T) => React.ReactNode
  /** Cell renderer */
  cell?: (row: T) => React.ReactNode
  /** Whether column is sortable */
  sortable?: boolean
  /** Column width class */
  className?: string
}

/**
 * Sort state
 */
export interface SortState {
  column: string
  direction: 'asc' | 'desc'
}

export interface DataTableProps<T> {
  /** Array of data to display */
  data: T[]
  /** Column definitions */
  columns: ColumnDef<T>[]
  /** Unique key extractor for rows */
  getRowKey: (row: T) => string
  /** Called when row is clicked */
  onRowClick?: (row: T) => void
  /** Loading state */
  isLoading?: boolean
  /** Message when no data */
  emptyMessage?: string
  /** Current sort state */
  sort?: SortState
  /** Called when sort changes */
  onSortChange?: (sort: SortState) => void
  /** Additional row class name */
  rowClassName?: string | ((row: T) => string)
}

/**
 * Generic data table with sorting support.
 * 
 * @example
 * ```tsx
 * <DataTable
 *   data={runs}
 *   columns={[
 *     { id: 'status', header: 'Status', cell: (run) => <RunStatusBadge status={run.status} /> },
 *     { id: 'id', header: 'Run ID', accessorFn: (run) => run.run_id },
 *     { id: 'goal', header: 'Goal', accessorFn: (run) => run.goal },
 *   ]}
 *   getRowKey={(run) => run.run_id}
 *   onRowClick={(run) => navigate(`/runs/${run.run_id}`)}
 * />
 * ```
 */
export function DataTable<T>({
  data,
  columns,
  getRowKey,
  onRowClick,
  isLoading,
  emptyMessage = 'No data found',
  sort,
  onSortChange,
  rowClassName,
}: DataTableProps<T>) {
  const handleSort = (columnId: string) => {
    if (!onSortChange) return
    
    if (sort?.column === columnId) {
      onSortChange({
        column: columnId,
        direction: sort.direction === 'asc' ? 'desc' : 'asc',
      })
    } else {
      onSortChange({ column: columnId, direction: 'asc' })
    }
  }

  const renderSortIcon = (columnId: string) => {
    if (sort?.column !== columnId) {
      return <ChevronsUpDown className="ml-2 h-4 w-4 text-muted-foreground/50" />
    }
    return sort.direction === 'asc' ? (
      <ChevronUp className="ml-2 h-4 w-4" />
    ) : (
      <ChevronDown className="ml-2 h-4 w-4" />
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-foreground">
        {emptyMessage}
      </div>
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map((column) => (
            <TableHead
              key={column.id}
              className={cn(
                column.sortable && onSortChange && 'cursor-pointer select-none',
                column.className
              )}
              onClick={() => column.sortable && handleSort(column.id)}
            >
              <div className="flex items-center">
                {column.header}
                {column.sortable && onSortChange && renderSortIcon(column.id)}
              </div>
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((row) => {
          const key = getRowKey(row)
          const rowClass = typeof rowClassName === 'function' 
            ? rowClassName(row) 
            : rowClassName
          
          return (
            <TableRow
              key={key}
              onClick={() => onRowClick?.(row)}
              className={cn(
                onRowClick && 'cursor-pointer',
                rowClass
              )}
            >
              {columns.map((column) => (
                <TableCell key={column.id} className={column.className}>
                  {column.cell 
                    ? column.cell(row) 
                    : column.accessorFn?.(row)}
                </TableCell>
              ))}
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

